<template>
  <ontos-base v-bind="baseProps" ref="base">
    <div v-if="loaded">
      <ontos-page-header :title="$tt('profile')" :level="1"/>

      <ontos-section :title="$tt('profile-identity')" :divider="false">
        <div class="d-flex align-start ga-6 flex-wrap">
          <!-- A foto à esquerda do nome, e não acima: é como a pessoa se vê na barra, e manter a
               mesma relação entre as duas coisas evita a surpresa de "ficou diferente lá". -->
          <div class="profile-photo">
            <ontos-media-field type="image" :value="photo" :label="$tt('photo')"
                               :error-message="photoError"
                               @input="photo = $event"/>
          </div>

          <div class="profile-fields">
            <!-- Quem chega aqui sem nome foi trazido pelo guarda das rotas: o nome é obrigatório na
                 conta (decisão do usuário), porque é por ele, e não pelo e-mail, que os outros a
                 veem nos ambientes. A frase diz por que a pessoa está aqui e não onde clicou. -->
            <div v-if="nameMissing" class="text-body-2 text-warning mb-3">{{ $tt('profile-name-needed') }}</div>
            <v-text-field v-model="name" :label="$tt('name')" autocomplete="name" class="required"
                          :error-messages="nameError"
                          @update:model-value="nameError = ''"/>

            <!-- O alias em leitura: é com ele que a pessoa é citada nos textos, e ele não muda (decisão do
                 usuário) — trocado, as citações apontariam para ninguém -->
            <ontos-view-info v-if="alias" :title="$tt('profile-alias')">
              <span>@{{ alias }}</span>
            </ontos-view-info>

            <!-- O e-mail em leitura: é o login. Trocá-lo é assunto do provedor de entrada, e não um
                 campo do perfil. -->
            <ontos-view-info :title="$tt('email')">
              <div class="d-flex align-center ga-2 flex-wrap">
                <span>{{ email }}</span>
                <v-chip v-if="verified" size="x-small" variant="flat" color="success">
                  {{ $tt('auth.verified') }}
                </v-chip>
                <v-btn v-else variant="text" size="x-small" class="profile-link"
                       @click="resendVerification()">
                  {{ $tt('auth.resend-verification') }}
                </v-btn>
              </div>
            </ontos-view-info>

            <div class="d-flex justify-end mt-4">
              <v-btn color="primary" size="small" :disabled="!changed" :loading="isSaving"
                     @click="save()">
                <v-icon start size="small">mdi-content-save</v-icon>
                {{ $tt('save') }}
              </v-btn>
            </div>
          </div>
        </div>
      </ontos-section>

      <!-- O idioma preferido da conta, que vale em todo aparelho em que a pessoa entrar (decisão do
           usuário). Aplica e grava NA ESCOLHA, sem o Salvar do nome e da foto: é uma preferência, e a
           resposta é a própria tela mudando de idioma. A primeira opção tira a preferência, e diz
           qual seria o do navegador. -->
      <ontos-section :title="$tt('language')">
        <div class="profile-fields">
          <v-select :model-value="language" :items="languages" :label="$tt('language')"
                    :loading="isSaving" @update:model-value="chooseLanguage($event)"/>
        </div>
      </ontos-section>

      <ontos-section :title="$tt('access')">
        <!-- O mesmo chip da tela de convites, com as mesmas cores: quem vê os dois reconhece. -->
        <ontos-colored-value v-if="access" :option="accessOption(access)" form="chip"/>
        <div class="text-body-2 text-grey mt-1">{{ $tt('profile-access-note') }}</div>
      </ontos-section>

      <!-- Só para quem entrou por senha: quem veio do Google não tem senha neste sistema, e um
           formulário de troca ali prometeria o que não existe. -->
      <ontos-section v-if="hasPassword" :title="$tt('profile-security')">
        <div class="profile-fields">
          <v-text-field v-model="currentPassword" :label="$tt('auth.current-password')"
                        type="password" autocomplete="current-password"
                        :error-messages="passwordErrors.current"
                        @update:model-value="passwordErrors.current = ''"/>

          <v-text-field v-model="newPassword" :label="$tt('auth.new-password')" type="password"
                        autocomplete="new-password" class="mt-2"
                        :error-messages="passwordErrors.next"
                        @update:model-value="passwordErrors.next = ''"/>

          <div class="d-flex justify-end mt-4">
            <v-btn color="primary" size="small" variant="tonal" :loading="isSaving"
                   @click="changePassword()">
              <v-icon start size="small">mdi-lock-reset</v-icon>
              {{ $tt('auth.change-password') }}
            </v-btn>
          </div>
        </div>
      </ontos-section>
    </div>
  </ontos-base>
</template>

<script lang="ts">
import {Component, toNative} from 'vue-facing-decorator'
import RouteComponent from '@/components/shared/RouteComponent'
import Section from '@/components/shared/Section.vue'
import ViewInfo from '@/components/shared/ViewInfo.vue'
import ColoredValue from '@/components/shared/ColoredValue.vue'
import MediaField from '@/components/shared/MediaField.vue'
import {Access, accessOption} from '@/types/Access'
import {auth} from '@/firebase'
import {isPending, revoke, takePending, upload, userPhotoPath} from '@/services/mediaStorage'
import {browserLanguage, LANGUAGE_NAMES} from '@/utils/language'

/** A opção "idioma do navegador" do seletor — ver o `language`. */
const BROWSER = 'browser'

/**
 * Os dados da própria pessoa.
 *
 * Tela de aplicação — sem `:system`, estende o `RouteComponent` —, alcançada pelo menu do avatar.
 *
 * **O Firebase é o dono do que está aqui.** Nome e foto são `displayName` e `photoURL`, gravados
 * pelo `updateProfile`; o servidor recebe os dois pelo token no `auth/sync` seguinte e guarda uma
 * cópia, que é o que faz este nome aparecer para OS OUTROS — nas pessoas de um ambiente e nos
 * convites. O nível de acesso é o contrário: mora só no servidor, e aqui é leitura.
 */
@Component({
  components: {
    'ontos-section': Section,
    'ontos-view-info': ViewInfo,
    'ontos-colored-value': ColoredValue,
    'ontos-media-field': MediaField
  }
})
class Profile extends RouteComponent {
  name = ''
  photo: string | null = null
  nameError = ''
  photoError = ''

  currentPassword = ''
  newPassword = ''
  passwordErrors = {current: '', next: ''}

  //exposto ao template: função de módulo não é enxergada de lá
  accessOption = accessOption

  /** O que estava guardado quando a tela abriu — é contra isto que se compara o que mudou. */
  private original = {name: '', photo: null as string | null}

  async load() {
    this.name = this.$storage.user?.name || ''
    this.photo = this.$storage.user?.photo || null
    this.original = {name: this.name, photo: this.photo}
  }

  get email(): string {
    return this.$storage.user?.email || ''
  }

  get alias(): string {
    return this.$storage.user?.alias || ''
  }

  /** A conta ainda não tem nome guardado — e por isso o guarda das rotas trouxe a pessoa aqui. */
  get nameMissing(): boolean {
    return !this.original.name
  }

  get access(): Access | undefined {
    return this.$storage.user?.access
  }

  get verified(): boolean {
    return !!auth.currentUser?.emailVerified
  }

  get hasPassword(): boolean {
    return this.$auth.hasPassword()
  }

  get changed(): boolean {
    return this.name !== this.original.name || this.photo !== this.original.photo
  }

  /**
   * Grava o perfil: a foto primeiro, o Firebase depois, o servidor por último.
   *
   * **A ordem não é preferência.** A foto escolhida é um `blob:` que só existe nesta aba; ela
   * precisa virar URL antes de ir para o `photoURL`. E o `updateProfile` do serviço força o
   * refresh do token antes de chamar o `sync`, senão o servidor regravaria o nome antigo.
   */
  async save() {
    this.nameError = this.required(this.name)
    if (this.nameError) return
    await this.saving(async () => {
      try {
        const photoURL = await this.storedPhoto()
        await this.$auth.updateProfile({displayName: this.name, photoURL: photoURL || ''})
        //Só depois de gravado: trocar a prévia antes de a URL existir deixaria o campo
        //apontando para um blob que ninguém mais acha
        if (photoURL && isPending(this.photo)) {
          const pending = this.photo as string
          this.photo = photoURL
          revoke(pending)
        }
        this.original = {name: this.name, photo: this.photo}
        this.info(this.$tt('info.profile-saved'))
      } catch (err) {
        this.throwError(this.$tt('error.save-profile'), err)
      }
    })
  }

  /** A foto como URL definitiva: sobe a pendente, mantém a que já estava, ou devolve nada. */
  private async storedPhoto(): Promise<string | null> {
    if (!isPending(this.photo)) return this.photo
    const blob = takePending(this.photo as string)
    //Sem o blob — a aba recarregou entre escolher e salvar — vale o que havia antes, e não um
    //`blob:` morto gravado no perfil
    if (!blob) return this.original.photo
    return await upload(userPhotoPath(auth.currentUser!.uid), blob)
  }

  /**
   * O idioma escolhido, ou `browser` sem preferência. Um valor próprio, e não `null`: o `v-select`
   * lê `null` como "nada escolhido", e a opção do idioma do navegador não apareceria marcada.
   */
  get language(): string {
    return this.$storage.user?.language || BROWSER
  }

  /** As opções: o do navegador, dizendo qual é, e cada idioma do app no próprio nome. */
  get languages() {
    return [
      {value: BROWSER, title: this.$tt('language-browser', {language: LANGUAGE_NAMES[browserLanguage()]})},
      ...Object.entries(LANGUAGE_NAMES).map(([value, title]) => ({value, title}))
    ]
  }

  async chooseLanguage(choice: string) {
    await this.saving(async () => {
      try {
        await this.$auth.setLanguage(choice === BROWSER ? null : choice)
        //Já no idioma novo: o `setLanguage` o aplicou antes
        this.info(this.$tt('info.language-saved'))
      } catch (err) {
        this.throwError(this.$tt('error.save-language'), err)
      }
    })
  }

  async changePassword() {
    this.passwordErrors.current = this.required(this.currentPassword)
    this.passwordErrors.next = this.required(this.newPassword)
    if (this.passwordErrors.current || this.passwordErrors.next) return
    await this.saving(async () => {
      try {
        await this.$auth.changePassword(this.currentPassword, this.newPassword)
        this.currentPassword = ''
        this.newPassword = ''
        this.info(this.$tt('info.password-changed'))
      } catch (err: any) {
        //A recusa no campo de que ela fala: senha atual errada é do primeiro campo, senha fraca
        //é do segundo
        if (['auth/wrong-password', 'auth/invalid-credential'].includes(err?.code))
          this.passwordErrors.current = this.$tt('auth.invalid-credentials') as string
        else if (err?.code === 'auth/weak-password')
          this.passwordErrors.next = this.$tt('auth.weak-password') as string
        else this.throwError(this.$tt('auth.generic-error'), err)
      }
    })
  }

  async resendVerification() {
    await this.saving(async () => {
      await this.$auth.sendEmailVerification()
      this.info(this.$tt('info.verification-sent'))
    })
  }
}

export default toNative(Profile)
</script>

<style scoped>
/* A caixa de mídia tem 180px de altura própria; aqui ela é um retrato, e 180 de largura a deixa
   quadrada em vez de deitada. */
.profile-photo {
  width: 180px;
}

/* Campo de formulário não cresce até a borda da tela: linha de entrada muito larga é
   desconfortável de ler e de preencher — a mesma medida do formulário de autenticação. */
.profile-fields {
  flex: 1;
  max-width: 420px;
  min-width: 260px;
}
</style>
